/**
 * Editor AI assistant: the vocabulary shared by the server, the editor and the page outline.
 *
 * The change kinds are the first explicit definition of what the injector's apply-changes
 * understands. dragPosition is deliberately absent: it encodes geometry (pixel offsets) the model
 * cannot reason about from an outline. A move is structural instead (a new parent and next
 * sibling), so it is offered, as the editor's own drag and drop records it: a domMove change.
 * Design: docs/frontend/editor-ai-assistant.md.
 */

/**
 * What a proposed change does. 'css' carries a property name next to the value. 'html' carries an
 * action (EDITOR_AI_HTML_ACTIONS). 'move' carries a target ref and a position and becomes a domMove
 * change. 'hide' is display: none, exactly the editor's own "Remove element" (the page closes up,
 * the element stays in the markup, so it is undone like any style).
 */
export const EDITOR_AI_CHANGE_KINDS = ['text', 'html', 'src', 'backgroundImage', 'css', 'move', 'hide'] as const;

/**
 * Where html goes, as the editor's code editor offers it (HTML_CODE_EDITOR_ACTIONS, the same six):
 * inner replaces the element's children and keeps the element; replace swaps the element for the
 * markup; insert and append put it first or last inside; before and after next to it. Every action
 * but inner inserts exactly one element (the injector takes the markup's first node).
 */
export const EDITOR_AI_HTML_ACTIONS = ['inner', 'replace', 'insert', 'append', 'before', 'after'] as const;

/** Where a move puts its element, relative to the target element. */
export const EDITOR_AI_MOVE_POSITIONS = ['before', 'after', 'firstInside', 'lastInside'] as const;

/**
 * When a proposal is a large page change, the card says so before Apply (page speed: every byte is
 * sent to every visitor, and a change applied after the page loads shifts the layout). Tunable.
 * - addedBytes: what a turn adds to the variant (values and AI scripts), in bytes.
 * - structuralChanges: moves, inserts, hides and replaces in one turn.
 * - largeSectionDescendants: replacing an element with more descendants than this, in the first screen.
 */
export const EDITOR_AI_IMPACT_LIMITS = {
  addedBytes: 25_000,
  structuralChanges: 10,
  largeSectionDescendants: 50,
} as const;

/** The model answers only through these. A free-text reply is never rendered. */
export const EDITOR_AI_TOOL_NAMES = ['propose_changes', 'propose_js', 'cannot_do', 'ask_user'] as const;

export const EDITOR_AI_MESSAGE_ROLES = ['user', 'assistant'] as const;

/** Where a turn ended. An aborted turn is one the client walked away from mid-stream. */
export const EDITOR_AI_USAGE_STATUSES = ['completed', 'errored', 'aborted'] as const;

export const EDITOR_AI_PROPOSAL_STATUSES = ['pending', 'accepted', 'rejected'] as const;

/**
 * What AI-written HTML does once it is on the page (helpers/ai-html-rules.ts). passive: markup
 * only. external: markup that loads images or media from other hosts. active: markup that can run
 * code or load active content (a script, an event handler, an iframe and so on), which is never
 * refused but needs the person's explicit confirm. HTML that cannot be applied at all is never a
 * proposal, so it has no class here.
 */
export const EDITOR_AI_HTML_CLASSES = ['passive', 'external', 'active'] as const;

/**
 * The first-use notice, keyed in user_acknowledgements. Bump the version when the wording
 * changes and everyone is asked again.
 */
export const EDITOR_AI_NOTICE_KEY = 'editor-ai';
export const EDITOR_AI_NOTICE_VERSION = 1;

/** The `feature` the AI credit ledger records an editor AI turn under. */
export const EDITOR_AI_CREDIT_FEATURE = 'editor-ai';

/**
 * The server's deadline for one turn's model call (run-model.ts). A full 16,000-token answer with
 * adaptive thinking takes a few minutes at Sonnet's output speed, and a deadline shorter than that
 * cuts off answers that would have finished (output that is still charged).
 */
export const EDITOR_AI_TURN_TIMEOUT_MS = 240_000;

/**
 * How long past that deadline a turn may still be busy: its thread, usage row and charge writes, and
 * the SSE writes after them. The server's turn locks expire after the two together
 * (turn-slots.ts), and the editor waits as long through the extension proxy (proxy-fetch.ts), so no
 * layer gives up before the server. The sum stays within Chrome's five minutes for one extension
 * service worker event, the ceiling the proxied request runs under.
 */
export const EDITOR_AI_TURN_GRACE_MS = 60_000;

export type EditorAiChangeKind = (typeof EDITOR_AI_CHANGE_KINDS)[number];
export type EditorAiHtmlAction = (typeof EDITOR_AI_HTML_ACTIONS)[number];
export type EditorAiMovePosition = (typeof EDITOR_AI_MOVE_POSITIONS)[number];
export type EditorAiToolName = (typeof EDITOR_AI_TOOL_NAMES)[number];
export type EditorAiMessageRole = (typeof EDITOR_AI_MESSAGE_ROLES)[number];
export type EditorAiUsageStatus = (typeof EDITOR_AI_USAGE_STATUSES)[number];
export type EditorAiProposalStatus = (typeof EDITOR_AI_PROPOSAL_STATUSES)[number];
export type EditorAiHtmlClass = (typeof EDITOR_AI_HTML_CLASSES)[number];

// ---------------------------------------------------------------------------------------------
// Page outline: built by the runtime, sent by the editor, validated by the server.
// ---------------------------------------------------------------------------------------------

export type EditorAiOutlineNode = {
  ref: string;
  tag: string;
  classes?: string[];
  text?: string;
  attrs?: Record<string, string>;
  /** The element shows in the first screen (before any scroll). Absent when it does not. */
  aboveFold?: boolean;
  /** How many elements it holds on the page (not only in the outline), when it holds many. */
  descendants?: number;
  children?: EditorAiOutlineNode[];
};

export type EditorAiOutline = {
  url: string;
  title?: string;
  root: EditorAiOutlineNode;
  truncated: boolean;
};

/**
 * Sent with a message that answers the assistant's question (ask_user). `label` is the option the
 * person chose, or null for an answer they typed (refused unless the question allows free text).
 * The server finds the question in the stored thread, sends the person's words as the request with
 * the question quoted beside it as data (`<answering>`), stores only the person's words as their
 * message, and records the answer on the question.
 */
export type EditorAiAnswerRef = { proposalId: string; label: string | null };

/**
 * What became of an earlier proposal, as the editor sees it when the next message is sent: still
 * on the page (applied), applied and then taken back (undone: undo, Revert, a removed script),
 * turned down (rejected), or never applied (not_applied). The thread's text replays an earlier
 * answer as if its change still stands, so the model is told this alongside it.
 */
export const EDITOR_AI_PROPOSAL_OUTCOMES = ['applied', 'undone', 'rejected', 'not_applied'] as const;
export type EditorAiProposalOutcome = (typeof EDITOR_AI_PROPOSAL_OUTCOMES)[number];

/** One earlier proposal's outcome. `turnsAgo` is 1 for the turn before this one. */
export type EditorAiProposalOutcomeNote = { turnsAgo: number; label: string; status: EditorAiProposalOutcome };

/** Kept short: only the newest turns' proposals, labels cut. The server enforces the same caps. */
export const EDITOR_AI_OUTCOME_LIMITS = { turns: 5, items: 20, labelChars: 80 } as const;

export type EditorAiChatBody = {
  variantId: number;
  message: string;
  outline: EditorAiOutline;
  selectionRef?: string;
  screenshot?: string;
  answer?: EditorAiAnswerRef;
  /** The outcome of the latest turns' proposals, read from the editor's change list at send time. */
  proposalOutcomes?: EditorAiProposalOutcomeNote[];
};

// ---------------------------------------------------------------------------------------------
// Stored and streamed proposals. Resolved data, never a bare ref: a ref only means something
// against the outline it came from.
// ---------------------------------------------------------------------------------------------

/**
 * Where a proposal landed on the page, as the editor's runtime resolved its ref. Stored with the
 * proposal because a ref only means something within the page lifetime that produced it, while
 * these fields find the element again after a reload.
 */
export type EditorAiChangeTarget = {
  selector: string;
  friendlySelector: string;
  friendlySelectorIndex: number;
  /** For a move: where it goes and where it came from, as the editor's drag and drop records them. */
  move?: EditorAiMoveFields;
};

/**
 * A domMove change's two halves, exactly as the drag and drop sends them (PERTENTO_DOM_MOVE): the
 * new place, described for the page before the move, and the original place, described for the
 * page after it (undo resolves it there). A null next sibling means last in the parent.
 */
export type EditorAiMoveFields = {
  newParentSelector: string | null;
  newNextSiblingSelector: string | null;
  originalParentSelector: string | null;
  originalNextSiblingSelector: string | null;
};

export type EditorAiChangeProposal = {
  type: 'change';
  /** Stable within the thread: `<turnId>:<index>`. */
  id: string;
  /** The outline ref the model chose. The editor resolves it to selector fields. */
  ref: string;
  kind: EditorAiChangeKind;
  cssProperty?: string;
  /** For html: where the markup goes. Absent on proposals from before actions, which were inner. */
  action?: EditorAiHtmlAction;
  /** For a move: the element it goes next to or into, and where. */
  targetRef?: string;
  position?: EditorAiMovePosition;
  /** For a move: the target element's tag and classes, from the outline, for the card. */
  targetTagName?: string;
  targetClasses?: string[];
  label: string;
  value: string;
  /** From the outline node the ref resolved to, so a stored proposal can show a before value. */
  tagName: string;
  classes?: string[];
  /** From the outline: the change touches an element in the first screen (for a move, either end). */
  aboveFold?: boolean;
  /** From the outline: how many elements the target holds, when it holds many (a replace's size). */
  descendants?: number;
  prevValue?: string;
  /**
   * For html: the class the server found (EDITOR_AI_HTML_CLASSES) and, when active, what makes it
   * active, as short phrases ("an onload handler", "an iframe from youtube.com"). The editor
   * classifies the value again and treats the proposal as active when either finds active parts.
   */
  htmlClass?: EditorAiHtmlClass;
  activeParts?: string[];
  /** Set by the editor once it has resolved the ref (POST /proposal-targets). Absent on a fresh proposal. */
  target?: EditorAiChangeTarget;
  status: EditorAiProposalStatus;
  /** The `changes` row an accepted proposal became. Set by the editor on accept. */
  changeId?: number;
  /** Who accepted or rejected it (a user id, from the session). Absent while pending. */
  decidedBy?: number;
};

export type EditorAiJsProposal = {
  type: 'js';
  id: string;
  code: string;
  description: string;
  status: EditorAiProposalStatus;
  /** Who accepted or rejected it (a user id, from the session). Absent while pending. */
  decidedBy?: number;
};

export type EditorAiCannotDo = {
  type: 'cannot_do';
  id: string;
  /** What the assistant cannot see or do, and any advice for the person: shown as text. */
  reason: string;
  /** Up to two answers the person can choose, each sent at once (as a question's plain options are). */
  suggestions: string[];
  /** Which suggestion was chosen, stored like a question's answer. */
  answer?: EditorAiAnswer;
};

/**
 * One answer the person can give to a question: a short label in the person's voice ("Use
 * placeholder reviews"). `needsInput` when choosing it means typing something only the person has
 * (the reviews themselves): the input opens for it, with `placeholder`, instead of sending at once.
 */
export type EditorAiAnswerOption = { label: string; needsInput: boolean; placeholder?: string };

/** How a question was answered, stored on the question so a reload shows it. */
export type EditorAiAnswer = {
  /** The turn the answer was sent as. Its user message is shown as this answer. */
  turnId: string;
  /** The option chosen. Absent for an answer the person typed in their own words. */
  label?: string;
  /** What the person typed: a free answer, or what a needsInput option asked for. */
  text?: string;
};

/** The assistant asks the person one thing it cannot go on without (ask_user). */
export type EditorAiQuestion = {
  type: 'question';
  id: string;
  question: string;
  /** Two to four, or none when only a typed answer makes sense. */
  options: EditorAiAnswerOption[];
  allowFreeText: boolean;
  answer?: EditorAiAnswer;
};

export type EditorAiBlockedProposal = { ref: string; reason: string };

/** The `ref` of a blocked entry for JavaScript, which has no outline ref. */
export const EDITOR_AI_BLOCKED_JS_REF = 'js';

export type EditorAiProposal = EditorAiChangeProposal | EditorAiJsProposal | EditorAiCannotDo | EditorAiQuestion;

// ---------------------------------------------------------------------------------------------
// SSE events sent by POST /api/editor-ai/chat. `event:` is the type, `data:` is the JSON payload.
// ---------------------------------------------------------------------------------------------

export type EditorAiStreamEvent =
  | { type: 'turn'; turnId: string }
  | { type: 'status'; turnId: string; step: string }
  | {
      type: 'proposals';
      turnId: string;
      /** One or two plain sentences on what the changes do and why. Also the stored message text. */
      summary: string;
      proposals: EditorAiChangeProposal[];
      /** Refs the model returned that are not in the submitted outline. */
      droppedRefs: string[];
      /** Proposals withheld because their content was unsafe, for example html with a script. */
      blocked: EditorAiBlockedProposal[];
    }
  | { type: 'js'; turnId: string; proposal: EditorAiJsProposal }
  | { type: 'cannot_do'; turnId: string; proposal: EditorAiCannotDo }
  | { type: 'question'; turnId: string; proposal: EditorAiQuestion }
  /**
   * The credits the turn has used so far, priced like the charge (turnCredits) from the token
   * counts reported so far: once when the model starts (input and cache), then at most about once
   * a second as the output grows, and once at the end with exactly what the turn is charged. Live
   * feedback only: the stored charge (GET /usage) is what the editor shows once the turn is over.
   */
  | { type: 'usage'; turnId: string; credits: number }
  | { type: 'error'; turnId: string; message: string; retryable: boolean }
  | { type: 'done'; turnId: string; status: EditorAiUsageStatus };

// ---------------------------------------------------------------------------------------------
// Tool schemas (Anthropic input_schema, JSON Schema).
//
// Strict (strict: true), so a tool call's input always matches its schema. Strict schemas take a
// subset of JSON Schema: every object sets additionalProperties: false and lists every property
// as required (a field that may be absent is required and nullable, or an array that may be
// empty), and array length limits are not supported. A nullable enum is an anyOf of the string
// enum and null: an `enum` beside `type: ['string', 'null']` is refused with a 400 on every request
// ("Enum value ... does not match declared type"). Those limits (at most 20 changes, at most
// two suggestions, two to four answer options) are enforced where the input is resolved, in
// resolve-proposals.ts and answers.ts.
// ---------------------------------------------------------------------------------------------

export type EditorAiToolSchema = {
  name: EditorAiToolName;
  description: string;
  strict: true;
  input_schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required: string[];
    additionalProperties: false;
  };
};

export const EDITOR_AI_TOOLS: EditorAiToolSchema[] = [
  {
    name: 'propose_changes',
    strict: true,
    description:
      'Propose one or more edits to elements on the page. Each change targets one element by its ref from the ' +
      'page outline. Use only refs that appear in the outline. Never write selectors.',
    input_schema: {
      type: 'object',
      properties: {
        summary: {
          type: 'string',
          description:
            'One or two plain sentences saying what the changes do and why. Write it in the same language as ' +
            "the person's message.",
        },
        changes: {
          type: 'array',
          description: 'One to twenty changes.',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              ref: { type: 'string', description: 'The ref of the target element, exactly as in the outline.' },
              kind: {
                type: 'string',
                enum: [...EDITOR_AI_CHANGE_KINDS],
                description:
                  'text replaces the visible text. html puts markup where action says (markup that runs code, such ' +
                  'as scripts, event handlers or iframes, needs the person to confirm it). src replaces an image or ' +
                  'media source. backgroundImage sets a background image URL. css sets one style property. move ' +
                  'moves the element to targetRef, at position. hide hides the element with display: none, so the ' +
                  'page closes up around it; it is how an element is removed, and it is undone like any style.',
              },
              label: {
                type: 'string',
                description: 'A short human label for the change card, for example "Shorter headline".',
              },
              value: {
                type: 'string',
                description:
                  'The new value: the text, HTML, URL or CSS value. An empty string for move and hide, which have none.',
              },
              cssProperty: {
                type: ['string', 'null'],
                description:
                  'When kind is css: the camelCase style property, for example "backgroundColor". null for every ' +
                  'other kind.',
              },
              action: {
                anyOf: [{ type: 'string', enum: [...EDITOR_AI_HTML_ACTIONS] }, { type: 'null' }],
                description:
                  'When kind is html: inner replaces the INNER HTML and keeps the element (the value is inner ' +
                  "markup, never the element's own tag); replace swaps the whole element for the value; insert " +
                  'and append put the value first or last inside the element; before and after put it next to ' +
                  'the element. For every action but inner the value is exactly ONE element (wrap several in one ' +
                  'container). null for every other kind.',
              },
              targetRef: {
                type: ['string', 'null'],
                description:
                  'When kind is move: the ref of the element to move next to or into, exactly as in the outline. ' +
                  'Never the moved element itself or anything inside it. null for every other kind.',
              },
              position: {
                anyOf: [{ type: 'string', enum: [...EDITOR_AI_MOVE_POSITIONS] }, { type: 'null' }],
                description:
                  'When kind is move: before or after the target element, or firstInside or lastInside it. null ' +
                  'for every other kind.',
              },
            },
            required: ['ref', 'kind', 'label', 'value', 'cssProperty', 'action', 'targetRef', 'position'],
          },
        },
      },
      required: ['summary', 'changes'],
      additionalProperties: false,
    },
  },
  {
    name: 'propose_js',
    strict: true,
    description:
      'Propose a block of JavaScript for this variant only when the request cannot be met with propose_changes. ' +
      "The code runs on every visitor's page, so it must be a self-contained script of at most 10000 " +
      'characters: no script tags, wrapped in one function with a try/catch, safe to run more than once ' +
      '(guard one-time setup with a unique flag on window), no reliance on DOMContentLoaded not having fired, ' +
      'no external network calls and no reading of cookies or storage unless the person asked for it. Prefer ' +
      'code that does its work directly over eval, new Function or loading other scripts: the person is warned ' +
      'about code that builds or loads more code at run time.',
    input_schema: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description: 'The JavaScript to run on the page: plain code, no script tags, at most 10000 characters.',
        },
        description: { type: 'string', description: 'One line saying what the code does.' },
      },
      required: ['code', 'description'],
      additionalProperties: false,
    },
  },
  {
    name: 'cannot_do',
    strict: true,
    description:
      'Decline when the request cannot be met reliably from the outline: content rendered at runtime, an element ' +
      'that is not in the outline, or an unclear request. Prefer this over guessing.',
    input_schema: {
      type: 'object',
      properties: {
        reason: { type: 'string', description: 'Why this cannot be done, in one or two plain sentences.' },
        suggestions: {
          type: 'array',
          items: { type: 'string' },
          description:
            "Up to two things you could do instead, each phrased as the person's own answer that you can act on " +
            'at once, at most 60 characters, for example "Use placeholder reviews" or "Skip the testimonials". ' +
            'Never an instruction to the person: advice belongs in the reason. An empty list when there is ' +
            'nothing to choose. When you need something from the person, call ask_user instead.',
        },
      },
      required: ['reason', 'suggestions'],
      additionalProperties: false,
    },
  },
  {
    name: 'ask_user',
    strict: true,
    description:
      'Ask the person one question, only when you genuinely cannot go on without them: the target is ambiguous, ' +
      'the content is something only they have, or there is a real either/or with different outcomes. Never to ' +
      'confirm a change you could propose: proposals are previewed and need Apply anyway. At most one per turn.',
    input_schema: {
      type: 'object',
      properties: {
        question: { type: 'string', description: 'The question, in one plain sentence, at most 300 characters.' },
        options: {
          type: 'array',
          description:
            "Two to four answers, each phrased as the person's own reply that you can act on, for example " +
            '"Use placeholder reviews" or "I\'ll paste reviews". Never an instruction to the person.',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              label: { type: 'string', description: 'The answer, at most 60 characters.' },
              needsInput: {
                type: 'boolean',
                description:
                  'True when choosing this answer means the person has to type something only they have, such as ' +
                  'pasting the reviews. False when the answer is complete as it is.',
              },
              placeholder: {
                type: ['string', 'null'],
                description:
                  'When needsInput: a short prompt for the input, for example "Paste the reviews…". null otherwise.',
              },
            },
            required: ['label', 'needsInput', 'placeholder'],
          },
        },
        allowFreeText: {
          type: 'boolean',
          description: 'True when the person may also answer in their own words instead of choosing an option.',
        },
      },
      required: ['question', 'options', 'allowFreeText'],
      additionalProperties: false,
    },
  },
];
